import { FormControl, FormLabel, HStack, Heading, IconButton, Switch, Text, Tooltip, useToast } from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FunctionComponent, ReactElement } from "react";
import { FaFileExport, FaSearch } from "react-icons/fa";

// Types
import { DataTableColumn, KVP, PaginatedApiResponse, PaginationResponse } from "../../../utils/types";
// import { DataTableColumn } from "../../DataTable/types";
import { useNavigate } from "react-router-dom";

interface DataTableHeaderActionsProps {
	meta: PaginationResponse;
	searchEnabled: boolean;
	exportEnabled: boolean;
	showFilterToggle: boolean;
	// advancedSearchEnabled: boolean;
	title?: string;
	customHeaderActions?: ReactElement;
	customRightActions?: ReactElement; // Actions to show on the right side, next to filter toggle
	customSelectedActions?: (selectedRows: Array<KVP>, clearSelectedRows: () => void) => ReactElement;

	getSelectedTableRows: () => Array<KVP>;
	clearSelectedRows: () => void;
	getCacheKey: () => string;
	getTableColumns: () => Array<DataTableColumn>;
	toggleSearch: () => void;
	// exportHandler: (key: string, meta: PaginationResponse) => Promise<PaginatedApiResponse | null>;
}

/**
 * Handles stock and custom actions associated with the given DataTable.
 * Stock options include Table Exports and enabling the Column Search feature.
 * Custom options can be added via the `customHeaderActions` optional property
 * @param props typeof: DataTableHeaderActionProps
 */
const DataTableHeaderActions: FunctionComponent<DataTableHeaderActionsProps> = (
	props: DataTableHeaderActionsProps
): ReactElement => {
	const {
		meta,
		searchEnabled,
		exportEnabled = true,
		showFilterToggle,
		// advancedSearchEnabled = true,
		title,
		customHeaderActions,
		customRightActions,
		customSelectedActions,
		getSelectedTableRows,
		clearSelectedRows,
		getCacheKey,
		getTableColumns,
		toggleSearch
		// exportHandler
	} = props;
	const navigate = useNavigate();

	/**
	 * Handles the logic of transforming returned API Data into CSV format for exporting
	 * @param data typeof: PaginatedApiResponse, returned from API Call
	 */
	const processTableExport = (data: PaginatedApiResponse) => {
		// Set some constants and prepare csv data
		let exportContent = "";
		const fileType = "data:text/csv;charset=utf-8,";
		const lineEnd = "\r\n";

		// Export header columns
		const columns: Array<DataTableColumn> = getTableColumns();
		exportContent += columns.map((column: DataTableColumn) => column.header).join(",") + lineEnd;

		// Export data rows
		const rowData =
			data.data?.map((row) => {
				const rowDataArray: Array<string | number> = [];
				columns.forEach((column: DataTableColumn) => {
					// We need to escape already existing commas
					// Wrap everything in quotes
					// eslint-disable-next-line quotes
					rowDataArray.push(`"${row[column.id]?.toString().replace(/\\"/g, '""') || ""}"`);
				});
				return rowDataArray;
			}) || [];

		// We need to do this in its own loop
		// For some reason, using .map produces preceeding commas?
		for (let i = 0; i < rowData.length; i++) {
			exportContent += rowData[i].join(",") + "\r\n";
		}

		// Construct the document to be exported
		const blob = new Blob([exportContent], { type: fileType });

		// Create an anonymous anchor link
		const link = document.createElement("a");
		link.setAttribute("href", URL.createObjectURL(blob));
		link.setAttribute("download", `LegalEASE - ${DateTime.now().toLocaleString()}.csv`);
		link.style.visibility = "hidden";

		// Append the anchor, click it, and remove it
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	/**
	 * Handles making the required call to the API to fetch all Table Data
	 */
	// const handleTableExport = async () => {
	// 	// Check if there are currently selected rows
	// 	// If there are, export those instead
	// 	const selectedRows = getSelectedTableRows();
	// 	if (selectedRows.length > 0) {
	// 		processTableExport({
	// 			data: selectedRows
	// 		} as PaginatedApiResponse);
	// 		return;
	// 	}

	// 	const exportHandlerResponse: PaginatedApiResponse | null = await exportHandler(getCacheKey(), meta);
	// 	if (exportHandlerResponse) processTableExport(exportHandlerResponse);
	// };

	return (
		<HStack justifyContent="space-between">
			<HStack>{customHeaderActions}</HStack>
			{title && (
				<Heading as="h4" fontSize="20">
					{title}
				</Heading>
			)}
			<HStack>
				{/* Add this in for the time being */}
				{/* {advancedSearchEnabled && location.pathname == "/attorneys/enabled" && (
					<FormControl display="flex" alignItems="center" onClick={() => navigate("/attorneys/advanced")}>
						<FormLabel htmlFor="advanced-view" mb="0" cursor="pointer">
							Advanced Search
						</FormLabel>
						<Switch colorScheme="orange" id="advanced-view" />
					</FormControl>
				)}
				{advancedSearchEnabled && location.pathname == "/attorneys/advanced" && (
					<FormControl display="flex" alignItems="center" onClick={() => navigate("/attorneys/enabled")}>
						<FormLabel htmlFor="advanced-view" mb="0" cursor="pointer">
							Advanced Search
						</FormLabel>
						<Switch colorScheme="orange" id="advanced-view" isChecked />
					</FormControl>
				)} */}
				{/* {exportEnabled && (
					<Tooltip hasArrow label="Export to CSV">
						<IconButton aria-label="Export to CSV" icon={<FaFileExport />} onClick={() => void handleTableExport()} />
					</Tooltip>
				)} */}
				{customSelectedActions &&
					typeof customSelectedActions === "function" &&
					customSelectedActions(getSelectedTableRows(), clearSelectedRows)}
				{customRightActions}
				{showFilterToggle && (
					<Tooltip label={searchEnabled ? "Hide Filters" : "Show Filters"}>
						<IconButton
							aria-label="Show Filters"
							size="sm"
							colorScheme={searchEnabled ? "orange" : "gray"}
							icon={<FaSearch />}
							onClick={() => toggleSearch()}
						/>
					</Tooltip>
				)}
			</HStack>
		</HStack>
	);
};

export default DataTableHeaderActions;
